import { LoadingOutlined } from '@ant-design/icons';
import { animated, useTransition } from '@react-spring/web';
import { Row } from 'antd';
import Spinner from 'common/spinner/components/spinner';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Button, Col, DropdownButton, media, Span, StateIcon, StateText } from 'ui';
import { orderStatus } from 'utils/app-configs';
import useLocalStorage from 'utils/hooks';
import { publishSteps, publishStepsByStatus } from '../../../utils/app-configs';
import useConfirmationMessage from '../../../utils/hooks/useConfirmationMessage';
import StatusBarMax from './status-bar-max';
import StatusBarMin from './status-bar-min';

const Title = styled.div`
  font-weight: ${p => p.active ? p.theme.fontWeight.bolder : p.theme.fontWeight.normal};
  color: ${p => p.active ? p.theme.colors.theme : p.theme.colors.text};
  font-size: 18px;
  line-height: 23px;
  width: 100%;
  text-transform: uppercase;
`;

const StyledStateIcon = styled(StateIcon)`
  margin-right: 5px;
`;

const StyledLoadingOutlined = styled(LoadingOutlined)`
  margin-right: 5px;
`;


const SubTitle = styled.div`
  font-weight: ${p => p.active ? p.theme.fontWeight.bold : p.theme.fontWeight.normal};
  color: ${p => p.active ? p.theme.colors.text : p.theme.colors.textLight};
  line-height: 20px;
`;

const Column = styled(Col)`
  ${media.tablet`
    padding-top: 5px;
    padding-bottom: 5px;`}
  height: 100%;
`;

const MinHeightRow = styled(Row)`
  min-height: 80px;
  margin-top: 56px;
`;

const ActionButtons = styled(Span)`
  position: absolute;
`;

const StyledRow = styled(Row)`
  height: 100%;
`;

const StatusBar = ({
  description,
  id,
  installedAt,
  plannedDate,
  progressInfo,
  hasMultipleOrders,
  isDirty,
  isSaving,
  isLocked,
  hasError,
  orderStates,
  activeStatus,
  intl,
  disabled,
  resetForm,
  removeOrder,
  save,
  operationInfo,
  hasMissingInfo,
}) => {
  const [showConfirm] = useConfirmationMessage(
    'order.CANCEL_CREATED_ORDER',
    'order.CANCEL_CREATED_ORDER_INFO',
    'common.NOT_NOW',
    'order.CANCEL_ORDER',
  );
  const [isCollapsed, setIsCollapsed] = useLocalStorage(
    'statusbarCollapsed',
    false,
  );

  const getButtonTranslation = (status, saveTo) => {
    if (status.saveTo[saveTo] === 'Save') {
      // concat "Save & the own entity (eg Save Draft or Save Planned)"
      return `${intl.formatMessage({
        id: `order.status.button.${status.saveTo[saveTo].toUpperCase()}`,
      })} ${intl
        .formatMessage({
          id: `order.status.${status.name.toUpperCase()}`,
        })
        .toUpperCase()}`;
    }
    return intl.formatMessage({
      id: `order.status.button.${status.saveTo[saveTo].toUpperCase()}`,
    });
  };

  const setAndSaveIsCollapsed = (value) => {
    setIsCollapsed(value);
  };

  const getSaveOptions = () =>
    Object.keys(activeStatus.saveTo)?.map(s => ({
      text: getButtonTranslation(activeStatus, s),
      onClick: s === orderStatus.closed.toString() ?
        () => showConfirm(() => save(s)) : () => save(s),
    }));

  const hasNoSaveToOptions = () =>
    !activeStatus || !activeStatus.saveTo || !Object.keys(activeStatus.saveTo).length;

  const renderOperationStatus = () => {
    const hasErrors = operationInfo?.errors?.length ||
      operationInfo?.isStale ||
      operationInfo?.isTimedOut;
    const isDone = operationInfo?.status === publishSteps.done.status;
    let severity = hasErrors ? 1 : 3;
    if (isDone) {
      severity = 4;
    }
    const icon = isDone ?
      <StyledStateIcon severity={severity} type={'check-circle'} /> :
      <StyledLoadingOutlined color='#0099cc' />;

    return <StateText severity={severity}>
      <Span font-size={12} data-test-id='sending-order-to-sap-state'>
        {hasErrors ?
          <StyledStateIcon severity={severity} type={'exclamation-circle'} /> :
          icon
        }
        {intl.formatMessage({
          id: `publishSteps.${publishStepsByStatus[operationInfo?.status]?.title}`,
        })}
      </Span>
    </StateText>;
  };

  const getDirtyIndicator = () => {
    if (hasNoSaveToOptions()) {
      return null;
    }

    if (hasError) {
      return (
        <StateText severity={1}>
          <Span font-size={12} marginRight={5}>
            <FormattedMessage id='order.status.error' />
          </Span>
          <StateIcon severity={1} type='times-circle' />
        </StateText>
      );
    }

    if (isSaving) {
      return (
        <StateText severity={2}>
          <Span font-size={12} marginRight={5}>
            <FormattedMessage id='order.status.saving' />
          </Span>
          <StateIcon severity={2} type='exclamation-triangle' />
        </StateText>
      );
    }
    if (hasMissingInfo) {
      return (
        <StateText severity={1}>
          <Span font-size={12} marginRight={5}>
            <FormattedMessage id='order.status.MISSING_INFO' />
          </Span>
          <StateIcon severity={1} type='times-circle' />
        </StateText>
      );
    }
    if (isDirty) {
      return (
        <StateText severity={2}>
          <Span font-size={12} marginRight={5}>
            <FormattedMessage id='order.status.pendingChanges' />
          </Span>
          <StateIcon severity={2} type='exclamation-triangle' />
        </StateText>
      );
    }
    if (isLocked) {
      if (
        operationInfo &&
        Object.keys(operationInfo)?.length &&
        operationInfo?.status !== publishSteps.done.status
      ) {
        return renderOperationStatus();
      }
      return (
        <StateText severity={2}>
          <Span font-size={12} marginRight={5}>
            <FormattedMessage id='order.status.locked' />
          </Span>
          <StateIcon severity={2} type='exclamation-triangle' />
        </StateText>
      );
    }
    return (
      <StateText severity={4}>
        <Span font-size={12} marginRight={5}>
          <FormattedMessage id='order.status.saved' />
        </Span>
        <StateIcon severity={4} type='check-circle' />
      </StateText>
    );
  };

  const getSaveButton = () => {
    if (hasNoSaveToOptions()) {
      return null;
    }

    const options = getSaveOptions(activeStatus);

    if (activeStatus.isDeleteAllowed) {
      options.push({
        text: intl.formatMessage({ id: 'order.status.button.REMOVE' }),
        onClick: removeOrder,
      });
    }

    if (isDirty) {
      options.push({
        text: intl.formatMessage({ id: 'order.status.button.UNDOCHANGES' }),
        onClick: () => resetForm(),
      });
    }

    return (
      <Col span={2} paddingBottom={8}>
        {options.length > 1 ?
          <DropdownButton
            disabled={disabled}
            text={options[0].text}
            onClick={options[0].onClick}
            subItems={options}
            placement='topRight'
          />
          :
          <Button type='secondary' disabled={disabled} onClick={options[0].onClick}>
            {options[0].text}
          </Button>
        }
      </Col>
    );
  };

  const getStatusses = () => {
    if (!orderStates || !activeStatus || !orderStates.length) {
      return <Spinner title={intl.formatMessage({ id: 'common.LOADING_STATUS' })} />;
    }

    return (
      <StyledRow gutter={8} type='flex' align='bottom'>
        {orderStates
          ?.filter(s => activeStatus.statusBar && activeStatus.statusBar.includes(s.id))
          ?.map(status =>
            <Column key={status.id} sm={6} xs={24}>
              {status.id === activeStatus.id &&
                <ActionButtons>
                  <Row>{getSaveButton()}</Row>
                  <Row>
                    <Col>{getDirtyIndicator()}</Col>
                  </Row>
                </ActionButtons>
              }
              <MinHeightRow active={status.id === activeStatus.id ? 1 : 0}>
                <Title
                  active={status.id === activeStatus.id ? 1 : 0}
                  data-test-id='status-bar.title'
                >
                  <FormattedMessage id={`order.status.${status.name}`} />
                </Title>
                <SubTitle
                  active={status.id === activeStatus.id ? 1 : 0}
                  data-test-id='status-bar.subtitle'
                >
                  <FormattedMessage id={`order.status.${status.name}_TEXT`} />
                </SubTitle>
              </MinHeightRow>
            </Column>,
          )}
      </StyledRow>
    );
  };

  const transitions = useTransition(isCollapsed, {
    from: {
      opacity: 0,
      height: '0px',
    },
    enter: {
      opacity: 1,
      height: isCollapsed ? '50px' : '180px',
    },
    leave: {
      opacity: 0,
      height: '0px',
    },
  });

  return transitions((props, item) =>
    !item ?
      <animated.div style={props}>
        <StatusBarMax
          orderstates={orderStates}
          activestatus={activeStatus}
          description={description}
          id={id}
          installedat={installedAt}
          planneddate={plannedDate}
          hasmultipleorders={hasMultipleOrders}
          statusses={getStatusses()}
          progressinfo={progressInfo({ hasChanges: isDirty, isSaving, item })}
          minimize={() => setAndSaveIsCollapsed(true)}
        />
      </animated.div>
      :
      <animated.div style={props}>
        <StatusBarMin
          orderstates={orderStates}
          activestatus={activeStatus}
          id={id}
          planneddate={plannedDate}
          hasmultipleorders={hasMultipleOrders}
          progressinfo={
            progressInfo({ hasChanges: isDirty, isSaving, item, isCollapsedStatusBar: true })
          }
          maximize={() => setAndSaveIsCollapsed(false)}
          getsavebutton={getSaveButton()}
          getdirtyindicator={getDirtyIndicator()}
        />
      </animated.div>
    ,
  );
};

StatusBar.propTypes = {
  description: PropTypes.string,
  id: PropTypes.string,
  installedAt: PropTypes.string,
  plannedDate: PropTypes.string,
  disabled: PropTypes.bool,
  save: PropTypes.func.isRequired,
  activeStatus: PropTypes.object,
  orderStates: PropTypes.array,
  isDirty: PropTypes.bool,
  isSaving: PropTypes.bool,
  isLocked: PropTypes.bool,
  hasError: PropTypes.bool,
  progressInfo: PropTypes.func,
  resetForm: PropTypes.func,
  removeOrder: PropTypes.func,
  hasMultipleOrders: PropTypes.bool,
  intl: PropTypes.object,
};

export default injectIntl(StatusBar);
